import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'

interface Props {
  /** 모달 제목용 원생 이름. 데이터는 placeholder라 learnerId 불필요(P7 후속에서 실데이터 배선). */
  learnerName: string
  onClose: () => void
}

/** 결정적 placeholder 행(직전 센터=퇴원 메모 있음 / 현재 센터=재원중·메모 없음). 시드 RNG·wireframe 금지. */
const ROW_KEYS = ['r1', 'r2'] as const
/** DRAFT_DOCS:284/387 표시 컬럼 순서. */
const COLS = ['no', 'period', 'center', 'course', 'curriculum', 'chapterLesson', 'memo'] as const

/**
 * 멀티센터 [재원 이력] 팝업 — 1차 UI(backend 추후). 읽기 전용 표 + "UI 미리보기" 배너.
 * 멀티센터 재원 이력 모델은 전무(commerce-payment-flow.md §11.7 Q2)하므로 데이터는 결정적 placeholder다.
 * 조상에 transform(animate-*)이 있으면 fixed dim 이 갇히는 함정을 피하려 document.body 로 포털한다.
 */
export function EnrollmentHistoryModal({ learnerName, onClose }: Props) {
  const { t } = useTranslation('admin')
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const tk = (s: string) => t(`students.enrollmentHistory.${s}`)

  // 마운트 1회: Escape 바인딩 + 패널 포커스, 언마운트 시 직전 포커스(트리거) 복원.
  // onClose 는 인라인 화살표라 매 렌더 신원이 바뀌므로 ref 로 잡아 effect 재실행(포커스 가로채기)을 막는다.
  useEffect(() => {
    const prevFocused = document.activeElement as HTMLElement | null
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      prevFocused?.focus?.()
    }
  }, [])

  const body = (
    <div
      data-testid="enrollment-history-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-lg font-bold text-gray-900">
            {t('students.enrollmentHistory.title', { name: learnerName })}
          </h2>
          <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
            {tk('previewBadge')}
          </span>
        </div>

        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-700">{tk('previewNotice')}</p>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {COLS.map((col) => (
                  <th
                    key={col}
                    scope="col"
                    className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase text-gray-500"
                  >
                    {tk(`col.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ROW_KEYS.map((rk, idx) => {
                const cell = (f: string) => tk(`rows.${rk}.${f}`)
                const memo = cell('memo')
                return (
                  <tr key={rk} className="text-gray-700">
                    <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                    <td className="whitespace-nowrap px-3 py-2">{cell('period')}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">{cell('center')}</td>
                    <td className="whitespace-nowrap px-3 py-2">{cell('course')}</td>
                    <td className="whitespace-nowrap px-3 py-2">{cell('curriculum')}</td>
                    <td className="whitespace-nowrap px-3 py-2">{cell('chapterLesson')}</td>
                    <td className="px-3 py-2 text-gray-600">{memo || tk('memoEmpty')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {tk('close')}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(body, document.body)
}
